// Image optimization utilities

/**
 * Check if browser supports WebP format
 */
export const supportsWebP = () => {
  if (typeof window === 'undefined') return false;
  
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
};

/**
 * Check if browser supports AVIF format
 */
export const supportsAVIF = () => {
  if (typeof window === 'undefined') return false;
  
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;
};

/**
 * Get optimized image URL based on browser support
 */
export const getOptimizedImageUrl = (originalUrl, options = {}) => {
  if (!originalUrl) return options.fallback || '/images/placeholder.jpg';
  
  // If it's already a data URL or external URL, return as is
  if (originalUrl.startsWith('data:') || originalUrl.startsWith('http')) {
    return originalUrl;
  }

  const { width, height, quality = 80, format } = options;
  
  // Determine best format based on browser support
  let preferredFormat = format;
  if (!preferredFormat) {
    if (supportsAVIF()) {
      preferredFormat = 'avif';
    } else if (supportsWebP()) {
      preferredFormat = 'webp';
    } else {
      preferredFormat = 'jpg';
    }
  }

  // Generate optimized URL
  let optimizedUrl = originalUrl;
  
  // Add format conversion if needed
  if (preferredFormat !== 'jpg' && !originalUrl.includes(`.${preferredFormat}`)) {
    optimizedUrl = originalUrl.replace(/\.(jpg|jpeg|png)$/i, `.${preferredFormat}`);
  }

  // Add query parameters for optimization
  const params = new URLSearchParams();
  if (width) params.append('w', width);
  if (height) params.append('h', height);
  if (quality) params.append('q', quality);
  
  if (params.toString()) {
    optimizedUrl += `?${params.toString()}`;
  }

  return optimizedUrl;
};

/**
 * Preload critical images
 */
export const preloadImage = (src, options = {}) => {
  if (typeof window === 'undefined') return Promise.resolve();
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = getOptimizedImageUrl(src, options);
  });
};

/**
 * Preload multiple images
 */
export const preloadImages = (imageUrls, options = {}) => {
  return Promise.all(imageUrls.map(url => preloadImage(url, options)));
};

/**
 * Generate responsive image srcset
 */
export const generateResponsiveSrcSet = (baseUrl, sizes = [320, 640, 1024, 1280, 1920]) => {
  const formats = [];
  
  // Generate WebP srcset
  if (supportsWebP()) {
    const webpSrcset = sizes
      .map(size => `${getOptimizedImageUrl(baseUrl, { width: size, format: 'webp' })} ${size}w`)
      .join(', ');
    formats.push({ srcset: webpSrcset, type: 'image/webp' });
  }
  
  // Generate fallback srcset
  const fallbackSrcset = sizes
    .map(size => `${getOptimizedImageUrl(baseUrl, { width: size })} ${size}w`)
    .join(', ');
  formats.push({ srcset: fallbackSrcset, type: 'image/jpeg' });
  
  return formats;
};

/**
 * Lazy load images with intersection observer
 */
export const setupLazyLoading = (selector = 'img[data-src]') => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    // Fallback for browsers without IntersectionObserver
    const images = document.querySelectorAll(selector);
    images.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    });
    return;
  }

  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (img.dataset.src) {
          img.src = getOptimizedImageUrl(img.dataset.src);
          img.removeAttribute('data-src');
          img.classList.add('loaded');
          observer.unobserve(img);
        }
      }
    });
  }, {
    rootMargin: '50px 0px',
    threshold: 0.01
  });

  const images = document.querySelectorAll(selector);
  images.forEach(img => imageObserver.observe(img));
};

/**
 * Optimize images on page load
 */
export const optimizePageImages = () => {
  if (typeof window === 'undefined') return;
  
  // Setup lazy loading
  setupLazyLoading();
  
  // Optimize existing images
  const images = document.querySelectorAll('img:not([data-optimized])');
  images.forEach(img => {
    if (img.src && !img.src.startsWith('data:') && !img.src.startsWith('http')) {
      const optimizedSrc = getOptimizedImageUrl(img.src);
      if (optimizedSrc !== img.src) {
        img.src = optimizedSrc;
        img.setAttribute('data-optimized', 'true');
      }
    }
  });
};
